import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import colors from '../../../../../../config/theme/colors';
import Unit from 'Components/Unit';
import ActionButton from 'Components/ActionButton';
import { ClearIconStyled } from '..';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TExaminationType } from '../../definitions/sleepApnea';
// import { examinationFieldToUnitMap } from '../../config/sleepApnea';

type TCodeName = NonNullable<TExaminationType['codeName']>;
type TFieldName = 'codeName' | 'minValue' | 'maxValue';

const codeNames: TCodeName[] = ['cB-pH', 'cB-pCO2', 'cB-pO2', 'cB-HCO3-st', 'cB-BE'];

const ExaminationsField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const criteria: TExaminationType[] = Array.isArray(searchFields.examinations)
    ? searchFields.examinations
    : [{ codeName: undefined, value: 'noCropping' }];

  const onChangeCriteria =
    (i: number) =>
    (values: TOnChangeValues): void => {
      const fieldName: TFieldName = Object.keys(values)[0] as TFieldName;
      const fieldValue: TFieldValue = values[fieldName];

      const newCriteria: TExaminationType[] = structuredClone(
        Array.isArray(searchFields.examinations) && searchFields.examinations.length > 0
          ? searchFields.examinations
          : [{ codeName: undefined, value: 'noCropping' }],
      );

      const current = newCriteria[i];

      if (fieldName === 'codeName') {
        current.codeName = fieldValue as TCodeName;
        current.value = 'noCropping';
      } else if (fieldName === 'minValue' || fieldName === 'maxValue') {
        const currentValue = current.value !== 'noCropping' ? current.value : {};
        const updated = { ...currentValue, [fieldName]: fieldValue };
        current.value = updated.minValue === undefined && updated.maxValue === undefined ? 'noCropping' : updated;
      }

      onChange({ examinations: newCriteria });
    };

  return (
    <React.Fragment>
      {criteria.map((item, i, arr) => {
        // const unit = item.codeName ? (examinationFieldToUnitMap.get(item.codeName) ?? '') : '';
        // TODO: Units left out for now "to be safe" -- remove entirely if it's decided for good that they won't be needed at all
        const unit = undefined;
        const minVal = item.value !== 'noCropping' ? item.value?.minValue : undefined;
        const maxVal = item.value !== 'noCropping' ? item.value?.maxValue : undefined;

        return (
          <React.Fragment key={`examinationCriteria${i}`}>
            <div
              style={{
                backgroundColor: colors.lightestGray,
                padding: '1rem',
                marginBottom: i === arr.length - 1 ? '1rem' : undefined,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginBottom: '1rem', color: colors.tertiaryText }}>
                  {fm('extendedSearch.searchCriteriaTitle')}
                  {` ${i + 1}`}
                </div>
                {arr.length > 1 && (
                  <ClearIconStyled
                    onClick={() => {
                      const filtered = arr.filter((_, index) => index !== i);
                      onChange({
                        examinations: filtered.length > 0 ? filtered : 'noCropping',
                      });
                    }}
                  />
                )}
              </div>
              <InputHandler
                type="Select"
                editing={true}
                name="codeName"
                formData={{
                  onChange: onChangeCriteria(i),
                  document: { codeName: item.codeName ?? null },
                }}
                options={codeNames}
                optionFormatter={(name: string | number): string | React.JSX.Element => `${name}`}
                placeholder={'extendedSearch.examinationCode'}
                fittedWidth
              />
              {item.codeName && (
                <React.Fragment>
                  <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 1rem 0' }}>
                    {fm('extendedSearch.result')}
                  </div>
                  <Unit unit={unit}>
                    <React.Fragment>
                      {['minValue', 'maxValue'].map((ff, ii) => (
                        <React.Fragment key={`${ff}-${ii}`}>
                          <InputHandler
                            type="NumberField"
                            editing={true}
                            name={ff}
                            formData={{
                              onChange: onChangeCriteria(i),
                              document: { [ff]: ff === 'minValue' ? minVal : maxVal },
                            }}
                            placeholder={`extendedSearch.${{ 0: 'min', 1: 'max' }[ii]}`}
                            width={9}
                            precision={2}
                          />
                          {ii === 0 && ' - '}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  </Unit>
                </React.Fragment>
              )}
            </div>
            {arr.length > 1 && i < arr.length - 1 && (
              <div style={{ padding: '1rem', textTransform: 'uppercase' }}>{fm('general.and')}</div>
            )}
          </React.Fragment>
        );
      })}
      <div style={{ marginTop: '2rem', justifyContent: 'center' }}>
        <ActionButton
          text={'extendedSearch.addSearchCriteria'}
          onClick={() => {
            const newCriteria = structuredClone(
              Array.isArray(searchFields.examinations) && searchFields.examinations.length > 0
                ? searchFields.examinations
                : [{ codeName: undefined, value: 'noCropping' }],
            );
            newCriteria.push({ codeName: undefined, value: 'noCropping' });
            onChange({ examinations: newCriteria });
          }}
          width={16}
          height={3}
          fontSize={16}
        />
      </div>
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default ExaminationsField;
